import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, User, Mail, Megaphone, Building2, CreditCard, Stethoscope, MonitorSmartphone, Eye, ClipboardCheck, QrCode, BellRing, ShieldCheck, Users } from 'lucide-react';
import PageHeader from './common/PageHeader';
import { getFeatures, isPatientIntakeOn, isClinicWalkinOn, isDeskReceptionOn, isPatientFeedbackOn, isDoctorCallOn, isOutsourceMfOn, isClinicPatientShareUiOn } from '../utils/features';
import { useRoadmapPilot } from '../utils/useRoadmapPilot';   // 🗺️ 진료 후 질문 카드 = 플래그 AND 파일럿 치과 (2026-10-06)
import { getVendorProfile } from '../services/outsourceMfApi';   // S5(10-27): 외주 프로필 보유 계정은 플래그 무관하게 「외주 수주 설정」 카드 노출(App.js 라우트 게이트와 같은 조건)

function SettingsHub({ user, userData }) {
    const navigate = useNavigate();
    // 🦷 치과 초대 플래그 (config/features.clinicInvite) — 훅은 항상 최상단에서 무조건 호출
    const [clinicInviteOn, setClinicInviteOn] = useState(false);
    // 🩺 첫 방문 등록(config/features.patientIntake, strict — 베타 미리보기 없음) ON일 때만 치과 설정 카드 노출 (2026-09-30)
    const [intakeOn, setIntakeOn] = useState(false);
    // 🪪 접수 QR — 웹 접수(clinicWalkin) 또는 데스크 접수(deskReception) 중 하나라도 ON이면 치과 설정 카드 노출 (2026-10-01, 10-04 deskReception 추가 — 같은 QR 화면)
    const [walkinOn, setWalkinOn] = useState(false);
    // 🩺 장착 후 확인(config/features.patientFeedback, strict + 환자관리 열림) ON일 때만 치과 설정 카드 노출 (2026-10-02)
    const [feedbackOn, setFeedbackOn] = useState(false);
    // 🔔 원장 호출(config/features.doctorCall strict) ON일 때만 치과 설정 카드 노출 (2026-10-08). 대표=변경, 직원=읽기 전용(페이지 안에서 분기)
    const [doctorCallOn, setDoctorCallOn] = useState(false);
    const [outsourceMfOn, setOutsourceMfOn] = useState(false);   // 외주 의뢰서 확장(10-13) — 「외주 수주 설정」(옛 외주 업체로 등록)
    const [hasVendorProfile, setHasVendorProfile] = useState(false);   // S5: vendorProfiles/{uid} 있음(approved·review·ended 등) → 플래그 OFF 여도 카드 노출
    // 🦷 환자 공개 범위(config/features.clinicPatientShareUi strict) ON일 때만 치과 설정 카드 노출 (2026-10-14). 대표=변경, 직원=읽기 전용(페이지 안에서 분기)
    const [patientShareOn, setPatientShareOn] = useState(false);
    // 🗺️ 진료 후 질문(config/features.roadmapEngine strict + 환자관리 열림 AND 파일럿 치과 — useRoadmapPilot, fail-closed) 일 때만 치과 **대표** 설정 카드 노출 (2026-10-06)
    const { on: roadmapOn } = useRoadmapPilot(userData || user);
    useEffect(() => {
        let alive = true;
        getFeatures().then(f => { if (alive) { setClinicInviteOn(f?.clinicInvite === true); setIntakeOn(isPatientIntakeOn(f)); setWalkinOn(isClinicWalkinOn(f) || isDeskReceptionOn(f)); setFeedbackOn(isPatientFeedbackOn(f, userData || user, (userData || user)?.email)); setDoctorCallOn(isDoctorCallOn(f)); setOutsourceMfOn(isOutsourceMfOn(f, userData || user, (userData || user)?.email)); setPatientShareOn(isClinicPatientShareUiOn(f)); } });
        return () => { alive = false; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const data = userData || user;
    const businessType = data?.businessType || data?.companyBusinessType;
    const isClinic = businessType === 'clinic' || businessType === 'dental' || businessType === '치과';
    const isLab = businessType === 'lab' || businessType === '기공소';
    const isClinicOrLab = isClinic || isLab;
    const isOutsource = businessType === 'outsource';
    const isNotStaff = data?.userType !== 'staff';
    const isStaff = data?.userType === 'staff';
    const vendorUid = (isLab || isOutsource) && isNotStaff && !data?.companyId ? data?.uid : null;
    useEffect(() => {
        if (!vendorUid) return undefined;
        let alive = true;
        getVendorProfile(vendorUid).then((p) => { if (alive) setHasVendorProfile(!!p); })
            .catch((e) => { console.error('[설정] 외주 프로필 확인 실패 code=%s', e && e.code); });
        return () => { alive = false; };
    }, [vendorUid]);

    // 이메일 파싱 기능 허용 대상: 아이반치과기공소(ivanlab@naver.com) 와 그 직원만
    const IVAN_LAB_UID = '76Y6wwTXUZcN7lbiHds3ZYSQcK92';
    const isIvanLab = (
        data?.email === 'ivanlab@naver.com' ||
        data?.uid === IVAN_LAB_UID ||
        data?.companyId === IVAN_LAB_UID
    );

    // 🌏 해외 계정: 영문 + 관련 카드만 (구독/PIN/이메일/홍보는 해당 없음)
    const isOverseas = data?.labKind === 'overseas';

    // 2026-07-19 메뉴 개편: 설정은 "계정·조직 관리"만 남김
    // - 거래명세서/통계/경영분석/매칭수수료 → 본 메뉴 (거래명세서·경영분석)
    // - 기공소 홍보/에이전트 다운로드 → 본 메뉴 (성장·자동화)
    // - 직원 관리 → 직원가입 오픈 전까지 임시 숨김
    const groups = isOverseas ? [
        {
            title: 'Partners',
            items: [
                { name: 'Partner labs', icon: Building2, path: '/connections', desc: 'Your connected Korean labs', show: true },
            ],
        },
        {
            title: 'Account',
            items: [
                { name: 'Billing', icon: CreditCard, path: '/settings/payment', desc: 'Billing profile printed on your invoices', show: true },
                { name: 'Profile', icon: User, path: '/profile', desc: 'Account information and password', show: true },
            ],
        },
    ] : [
        {
            title: '거래처 · 직원',
            items: [
                // 1차(거래명세서 서비스): 기공소는 아이반(베타)만 노출 — 일반 기공소 숨김 (2026-07-23)
                { name: '거래처 관리', icon: Building2, path: '/connections', desc: '거래처 목록 및 관리', show: ((isClinic || isOutsource) || (isLab && isIvanLab)) && isNotStaff },
                // 🦷 치과 초대 (features.clinicInvite ON일 때만 노출)
                { name: '치과 초대', icon: Stethoscope, path: '/clinic-invites', desc: '거래 치과를 초대해 웹 주문을 받으세요', show: isLab && isNotStaff && clinicInviteOn },
                // 직원 관리 — 07-19 임시 숨김을 해제(대표 10-12 "권고대로": 앱에서만 관리되던 상태 해소)
                { name: '직원 관리', icon: Users, path: '/company-manager', desc: '직원 목록 및 관리', show: (isClinicOrLab || isOutsource) && isNotStaff },
            ],
        },
        {
            title: '홍보',
            items: [
                // 기공소 홍보는 본 메뉴 "성장 → 내 기공소 소개"로 이동 (2026-07-19)
                { name: '외주업체 홍보', icon: Megaphone, path: '/outsource-advertising', desc: '외주업체 홍보 및 소개 관리', show: isOutsource },
            ],
        },
        {
            title: '계정 · 보안',
            items: [
                // 「대표 메뉴 잠금 (PIN)」 별도 카드 제거 (2026-08-21 대표 지시) — 프로필과 같은 페이지로 가는
                // 중복 진입점이었음(PIN 설정 실체는 프로필 안). 발견성은 프로필 설명에 PIN을 명시해 유지.
                { name: '프로필', icon: User, path: '/profile', desc: '개인정보 · 계정 · 대표 메뉴 잠금(PIN) 설정', show: true },
                // 결제 관리(글로벌 수취 정보) 버튼 제거 — 대표 지시 2026-10-04. /settings/payment 라우트·해외 Billing 버튼은 유지
                { name: '결제 관리', icon: CreditCard, path: '/settings/payment', desc: '글로벌 수취 정보 · 자동결제(준비 중)', show: false },
                // 🦷 치과는 전 기능 무료라 구독 없음 — 기공소(월 33,000원)에만 노출 (대표 확정 2026-08-14). 코드·라우트는 보존.
                // 치과도 노출 (2026-08-17 요금 단일화 — 시행 전엔 화면에 '무료' 안내, 청구는 서버 가드가 차단)
                // 2026-10-04 요금 변경(계획 W7): allFree 숨김 조건 제거 — 결제 화면 문구 "해지: 설정 › 구독 관리" 경로가 항상 보여야 함
                { name: '구독 관리', icon: CreditCard, path: '/subscription', desc: '구독 현황 · 결제 내역 · 해지', show: isClinicOrLab && isNotStaff },
                { name: '소속 관리', icon: Building2, path: '/staff-affiliation', desc: '소속 업체 해제 및 가입 신청', show: isStaff || data?.userType === 'individual' },
                // 1차: 이메일 의뢰서 연동은 아이반(베타)만 — 일반 기공소 숨김 (2026-07-23)
                { name: '이메일 설정', icon: Mail, path: '/email-settings', desc: '이메일 의뢰서 자동 연동', show: isLab && isNotStaff && isIvanLab },
                // 🖥️ 접속 PC 관리 — 덴클 프로그램 원격 로그아웃 1단계 (2026-08-06, 기공소 대표 전용)
                // 🏭 외주 의뢰서 확장(10-13) → S5(10-27) 「외주 수주 설정」: 신규 등록은 가입 승인에서, 여기서는 종류·소개·단가표·보류 해제 서류·시작·종료 — 대표만, outsourceMf ON 또는 베타
                { name: '외주 수주 설정', icon: Building2, path: '/settings/outsource-vendor', desc: '다른 기공소의 외주를 받는 설정 — 받을 종류·소개·단가표, 외주 수주 시작·종료', show: (isLab || isOutsource) && isNotStaff && (outsourceMfOn || hasVendorProfile) },
                { name: '접속 PC 관리', icon: MonitorSmartphone, path: '/settings/devices', desc: '이 계정으로 연결된 PC 확인 · 원격 로그아웃', show: isLab && isNotStaff },
                // 🦷 치과 공정 공개 (2026-08-17 대표 승인, 2026-09-02부터 기본 켜짐 — 끈 기공소만 비공개)
                // 💰 2026-10-07: 치과별 「예상 기공비·예상 명세서 표시」 스위치도 이 페이지에 동거 (스위치 하나로 월별 금액·기간 품목 둘 다)
                { name: '치과에 공정 공개', icon: Eye, path: '/settings/clinic-progress', desc: '케이스 진행 · 디자인 3D 미리보기 · 예상 기공비·예상 명세서(기간별 환자·품목·단가·금액)를 거래 치과에 공개 (기본 켜짐, 치과별 끄기 가능)', show: isLab && isNotStaff },
                // 🩺 첫 방문 등록 항목 (2026-09-30 대표 승인) — 플래그 ON인 치과만. 대표=변경, 직원=읽기 전용(페이지 안에서 분기)
                { name: '첫 방문 등록 항목', icon: ClipboardCheck, path: '/settings/intake-fields', desc: '환자앱 첫 방문 등록에서 추가로 요청할 항목', show: isClinic && intakeOn },
                // 🪪 웹 접수 QR (2026-10-01 대표 확정) — 플래그 ON인 치과만. 대표=발급, 직원=읽기 전용(페이지 안에서 분기)
                // 🩺 장착 후 확인 (2026-10-02 대표 승인) — 플래그 ON인 치과만. 대표=변경, 직원=읽기 전용(페이지 안에서 분기)
                { name: '장착 후 확인', icon: ClipboardCheck, path: '/settings/feedback', desc: '보철 장착 후 환자 앱으로 상태를 묻는 시점(3일·2주·1개월·6개월)', show: isClinic && feedbackOn },
                // 🗺️ 진료 후 질문 (2026-10-06, 플래그 roadmapEngine strict) — 치과 **대표만**(설정·트랙 종료 = 대표, 계약 권한 줄). 직원은 페이지 진입 시 읽기 전용
                { name: '진료 후 질문', icon: ClipboardCheck, path: '/settings/roadmap', desc: '[진료 끝]·[장착 완료] 뒤 환자 앱으로 나가는 질문의 시점·휴진일 (대표만)', show: isClinic && isNotStaff && roadmapOn },
                // 🛡️ 환자 정보 처리위탁 (2026-10-08 대표 "권고대로") — 데스크 접수·환자 앱 등록 처리위탁 확인 2건 상태·확인·환자 앱 철회. 치과 전원(직원은 상태만)
                { name: '환자 정보 처리위탁', icon: ShieldCheck, path: '/settings/clinic-delegation', desc: '데스크 접수·환자 앱 등록 처리위탁 확인 상태와 확인일, 환자 앱 등록 확인 철회', show: isClinic },
                // 🦷 환자 공개 범위 (2026-10-14, 플래그 clinicPatientShareUi strict) — 치과용 메뉴 설명(환자 노출 아님, 금지어 0)
                { name: '환자 공개 범위', icon: Eye, path: '/settings/patient-share', desc: '환자 앱 보철 기록에 재료·기공소명을 보일지, 디자인 완료 알림을 보낼지 (대표만 변경)', show: isClinic && patientShareOn },
                { name: '데스크 접수 QR', icon: QrCode, path: '/settings/walkin-qr', desc: '데스크에 붙이는 치과 고정 QR — 환자가 휴대폰으로 찍어 대기 순서를 받습니다', show: isClinic && walkinOn },
                // 🔔 원장 호출 (2026-10-08, 플래그 doctorCall strict)
                { name: '원장 호출', icon: BellRing, path: '/settings/doctor-call', desc: '진료실 호출 격자의 체어 수·상황 문구·응답 선택지·미응답 알림 시간, 주간 집계', show: isClinic && doctorCallOn },
            ],
        },
    ];

    return (
        <div style={S.container}>
            <PageHeader icon={Settings} title={isOverseas ? 'Settings' : '설정'} />

            {groups.map(group => {
                const visibleItems = group.items.filter(i => i.show);
                if (visibleItems.length === 0) return null;
                return (
                    <div key={group.title} style={S.group}>
                        <h3 style={S.groupTitle}>{group.title}</h3>
                        <div style={S.grid}>
                            {visibleItems.map(item => {
                                const Icon = item.icon;
                                return (
                                    <button key={item.path} onClick={() => navigate(item.path)} style={S.card}>
                                        <div style={S.iconBox}>
                                            <Icon size={22} color="#6366f1" />
                                        </div>
                                        <div style={S.cardContent}>
                                            <span style={S.cardName}>{item.name}</span>
                                            <span style={S.cardDesc}>{item.desc}</span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

const S = {
    container: { padding: '24px', maxWidth: 960, margin: '0 auto' },
    group: { marginBottom: '28px' },
    groupTitle: {
        margin: '0 0 12px',
        fontSize: '13px',
        fontWeight: '700',
        color: '#94a3b8',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        paddingLeft: '4px',
    },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 },
    card: {
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '16px', backgroundColor: '#fff', border: '1px solid #e2e8f0',
        borderRadius: 12, cursor: 'pointer', textAlign: 'left',
        transition: 'all 0.15s',
    },
    iconBox: {
        width: 44, height: 44, backgroundColor: '#eef2ff', borderRadius: 11,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    },
    cardContent: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
    cardName: { fontSize: 14, fontWeight: 600, color: '#0f172a' },
    cardDesc: { fontSize: 12, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
};

export default SettingsHub;
